import React, { useState, useEffect, useCallback } from 'react';

const MergePopup = ({ onMergeConfirm }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [mergeData, setMergeData] = useState(null);

  // Listen for merge popup requests
  useEffect(() => {
    const handleShowMergePopup = (event) => {
      const { draggedShape, targetShape, snapInfo, snapPosition } = event.detail;
      
      console.log('🎯 MERGE POPUP TRIGGERED:', {
        draggedShape: { id: draggedShape.id, type: draggedShape.type },
        targetShape: { id: targetShape.id, type: targetShape.type },
        snapType: snapInfo.snapType,
        snapPosition,
        timestamp: new Date().toLocaleTimeString()
      });
      
      setMergeData({
        draggedShape,
        targetShape,
        snapInfo,
        snapPosition
      });

      // Position popup near the target shape
      setPopupPosition({
        x: targetShape.x + targetShape.width / 2,
        y: targetShape.y - 60 // Above the shape
      });
      
      setShowPopup(true);
    };

    const handleHideMergePopup = () => {
    //   setShowPopup(false);
    //   setMergeData(null);
    };

    window.addEventListener('showMergePopup', handleShowMergePopup);
    window.addEventListener('hideMergePopup', handleHideMergePopup);

    return () => {
      window.removeEventListener('showMergePopup', handleShowMergePopup);
      window.removeEventListener('hideMergePopup', handleHideMergePopup);
    };
  }, []);

  const handleConfirm = useCallback(() => {
    console.log('✅ MERGE CONFIRMED BY USER:', {
      mergeData,
      timestamp: new Date().toLocaleTimeString()
    });
    
    if (mergeData && onMergeConfirm) {
      onMergeConfirm(mergeData);
    }
    setShowPopup(false);
    setMergeData(null);
  }, [mergeData, onMergeConfirm]);

  const handleCancel = useCallback(() => {
    setShowPopup(false);
    setMergeData(null);
  }, []);

  // Auto-hide popup after a delay if no action taken
  useEffect(() => {
    if (!showPopup) return;

    // Removed auto-hide timer - popup will only close via button clicks

  }, [showPopup]);

  // Handle keyboard events
  useEffect(() => {
    if (!showPopup) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleCancel();
      } else if (event.key === 'Enter' || event.key === ' ') {
        handleConfirm();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showPopup, handleConfirm, handleCancel]);

  if (!showPopup || !mergeData) return null;

  const getSnapDescription = (snapInfo) => {
    const { snapType } = snapInfo;
    
    const descriptions = {
      'top-to-bottom': 'Snap to bottom edge',
      'bottom-to-top': 'Snap to top edge',
      'left-to-right': 'Snap to right edge',
      'right-to-left': 'Snap to left edge'
    };

    return descriptions[snapType] || 'Snap shapes together';
  };

  const getMergeMessage = (mergeData) => {
    if (!mergeData) return { title: 'Snap Shapes Together?', description: 'This will create a merged structure.' };
    
    const isAddingToStructure = mergeData.targetShape.type === 'mergedStructure';
    
    if (isAddingToStructure) {
      const structureShapeCount = mergeData.targetShape.shapesCount || 'multiple';
      return {
        title: 'Add to Merged Structure?',
        description: `This will add the shape to an existing structure with ${structureShapeCount} shapes.`
      };
    } else {
      return {
        title: 'Snap Shapes Together?',
        description: 'This will create a new merged structure that can be moved together.'
      };
    }
  };

  const popupStyle = {
    position: 'fixed',
    left: `${popupPosition.x}px`,
    top: `${popupPosition.y}px`,
    transform: 'translateX(-50%)',
    background: 'linear-gradient(135deg, #1a1a1a 0%, #404040 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    padding: '16px 20px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    zIndex: 1000,
    fontFamily: 'Arial, sans-serif',
    fontSize: '14px',
    minWidth: '250px',
    maxWidth: '350px',
    animation: 'slideInFromTop 0.3s ease-out'
  };

  const headerStyle = {
    marginBottom: '8px',
    fontWeight: 'bold',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const descriptionStyle = {
    marginBottom: '16px',
    fontSize: '13px',
    opacity: 0.9,
    lineHeight: '1.4'
  };

  const buttonContainerStyle = {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end'
  };

  const buttonStyle = {
    padding: '8px 16px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 'bold',
    transition: 'all 0.2s ease'
  };

  const confirmButtonStyle = {
    ...buttonStyle,
    background: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    backdropFilter: 'blur(10px)'
  };

  const cancelButtonStyle = {
    ...buttonStyle,
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'rgba(255, 255, 255, 0.8)'
  };

  return (
    <>
      <style>
        {`
          @keyframes slideInFromTop {
            from {
              opacity: 0;
              transform: translateX(-50%) translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
          }
        `}
      </style>
      <div style={popupStyle} data-merge-popup>
        <div style={headerStyle}>
          <span>🔗</span>
          {getMergeMessage(mergeData).title}
        </div>
        <div style={descriptionStyle}>
          {getMergeMessage(mergeData).description}
        </div>
        <div style={buttonContainerStyle}>
          <button 
            style={cancelButtonStyle}
            onClick={handleCancel}
            onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.15)'}
            onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
          >
            Cancel
          </button>
          <button 
            style={confirmButtonStyle}
            onClick={handleConfirm}
            onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            {mergeData && mergeData.targetShape.type === 'mergedStructure' ? 'Add to Structure' : 'Snap Together'}
          </button>
        </div>
      </div>
    </>
  );
};

export default MergePopup; 